'use client';

import { useEffect, useRef, useState, type ReactNode } from 'react';
import Link from 'next/link';
import { Loader2, Share2, XCircle } from 'lucide-react';
import { useBasePath } from '../navigation/base-path';
import { S3Image } from './s3-image';
import { TrendVideoMedia } from './trend-video-media';
import { VideoPlayer } from './video-player';
import { cn } from '../utils/cn';

export interface TrendDetailScreenExample {
  id: string;
  imageUrl: string;
  thumbnailUrl?: string | null;
  isVideo: boolean;
}

export interface TrendDetailScreenItem {
  id: string;
  name: string;
  description?: string | null;
  examples: TrendDetailScreenExample[];
  model?: {
    pricePerGeneration?: string | null;
  } | null;
}

export interface TrendDetailScreenRelatedItem {
  id: string;
  name: string;
  examples: TrendDetailScreenExample[];
}

interface TrendDetailScreenProps {
  trend?: TrendDetailScreenItem | null;
  relatedTrends?: TrendDetailScreenRelatedItem[];
  isLoading?: boolean;
  hasError?: boolean;
  isPlaceholderData?: boolean;
  onBack: () => void;
  onShare: () => void;
  onRepeat: () => void;
  onDotSelect?: (index: number) => void;
  footer?: ReactNode;
}

export function TrendDetailScreen({
  trend,
  relatedTrends = [],
  isLoading = false,
  hasError = false,
  isPlaceholderData = false,
  onBack,
  onShare,
  onRepeat,
  onDotSelect,
  footer,
}: TrendDetailScreenProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [videoReadySlides, setVideoReadySlides] = useState<Set<number>>(
    () => new Set(),
  );
  const [dragOffset, setDragOffset] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const directionLocked = useRef<'h' | 'v' | null>(null);
  const isDragging = useRef(false);

  useEffect(() => {
    setCurrentImageIndex(0);
    setVideoReadySlides(new Set());
    setDragOffset(0);
    setIsAnimating(false);
  }, [trend?.id]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !trend) return;

    const onTouchStart = (event: TouchEvent) => {
      if (isAnimating) return;
      const touch = event.touches[0];
      if (!touch) return;
      touchStartX.current = touch.clientX;
      touchStartY.current = touch.clientY;
      directionLocked.current = null;
      isDragging.current = true;
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!isDragging.current || isAnimating) return;
      const touch = event.touches[0];
      if (!touch) return;

      const dx = touch.clientX - touchStartX.current;
      const dy = touch.clientY - touchStartY.current;

      if (!directionLocked.current) {
        if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
          directionLocked.current = Math.abs(dx) >= Math.abs(dy) ? 'h' : 'v';
        }
        return;
      }

      if (directionLocked.current === 'v') return;

      event.preventDefault();

      const atStart = currentImageIndex === 0 && dx > 0;
      const atEnd =
        currentImageIndex === trend.examples.length - 1 && dx < 0;
      const dampened = atStart || atEnd ? dx * 0.3 : dx;

      setDragOffset(dampened);
    };

    const onTouchEnd = () => {
      if (!isDragging.current || isAnimating) return;
      isDragging.current = false;

      if (directionLocked.current !== 'h') {
        setDragOffset(0);
        return;
      }

      const containerWidth = el.offsetWidth || 300;
      const threshold = containerWidth * 0.2;

      if (Math.abs(dragOffset) > threshold) {
        if (dragOffset < 0 && currentImageIndex < trend.examples.length - 1) {
          setIsAnimating(true);
          setDragOffset(-containerWidth);
          setTimeout(() => {
            setCurrentImageIndex((prev) => prev + 1);
            setDragOffset(0);
            setIsAnimating(false);
          }, 300);
          return;
        }

        if (dragOffset > 0 && currentImageIndex > 0) {
          setIsAnimating(true);
          setDragOffset(containerWidth);
          setTimeout(() => {
            setCurrentImageIndex((prev) => prev - 1);
            setDragOffset(0);
            setIsAnimating(false);
          }, 300);
          return;
        }
      }

      setDragOffset(0);
    };

    el.addEventListener('touchstart', onTouchStart, { passive: true });
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    el.addEventListener('touchend', onTouchEnd, { passive: true });

    return () => {
      el.removeEventListener('touchstart', onTouchStart);
      el.removeEventListener('touchmove', onTouchMove);
      el.removeEventListener('touchend', onTouchEnd);
    };
  }, [currentImageIndex, dragOffset, isAnimating, trend]);

  const markVideoReady = (index: number) => {
    setVideoReadySlides((prev) => {
      if (prev.has(index)) return prev;
      const next = new Set(prev);
      next.add(index);
      return next;
    });
  };

  const handleDotSelect = (index: number) => {
    setCurrentImageIndex(index);
    onDotSelect?.(index);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (hasError || !trend) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center p-4">
        <XCircle className="h-12 w-12 text-red-500" />
        <p className="mt-4 text-center text-muted-foreground">Тренд не найден</p>
        <button
          onClick={onBack}
          className="mt-4 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground"
        >
          Назад
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background pb-8">
      <div className="relative z-10 flex h-[50px] items-center justify-between bg-background px-4">
        <button
          onClick={onBack}
          className="detail-back-button flex h-9 w-9 items-center justify-center rounded-full bg-[#242424]/40"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="#fafafa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M11 4L5 10L11 16" />
            <line x1="5" y1="10" x2="16" y2="10" />
          </svg>
        </button>

        <button
          onClick={onShare}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-[#242424]/40"
        >
          <Share2 className="h-5 w-5 text-white" />
        </button>
      </div>

      {trend.examples.length > 0 && (
        <div className="relative w-full">
          <div
            ref={containerRef}
            className="relative w-full overflow-hidden touch-pan-y"
          >
            <div
              className="flex"
              style={{
                width: `${trend.examples.length * 100}%`,
                transform: `translateX(calc(-${
                  currentImageIndex * (100 / trend.examples.length)
                }% + ${dragOffset}px))`,
                transition:
                  isAnimating || dragOffset === 0
                    ? 'transform 300ms ease-out'
                    : 'none',
                willChange: 'transform',
              }}
            >
              {trend.examples.map((example, index) => (
                <div
                  key={example.id}
                  className="relative flex-shrink-0"
                  style={{ width: `${100 / trend.examples.length}%` }}
                >
                  {example.isVideo ? (
                    <div className="relative w-full bg-black">
                      {example.thumbnailUrl ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                          src={example.thumbnailUrl}
                          alt=""
                          className="block h-auto w-full"
                        />
                      ) : (
                        <div style={{ aspectRatio: '9/16' }} />
                      )}
                      {!videoReadySlides.has(index) && (
                        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                          <Loader2 className="h-8 w-8 animate-spin text-white/30" />
                        </div>
                      )}
                      {!isPlaceholderData && (
                        <div className="absolute inset-0">
                          <VideoPlayer
                            src={example.imageUrl}
                            poster={example.thumbnailUrl}
                            autoPlay={index === currentImageIndex}
                            loop
                            className="h-full w-full"
                            videoClassName="h-full w-full object-contain"
                            preload={index === currentImageIndex ? 'auto' : 'metadata'}
                            onLoadedData={() => markVideoReady(index)}
                            onCanPlay={() => markVideoReady(index)}
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <S3Image
                      src={example.imageUrl}
                      alt={`${trend.name} - пример ${index + 1}`}
                      width={588}
                      height={588}
                      className="h-auto w-full"
                      priority={index === currentImageIndex}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {trend.examples.length > 1 && (
            <div className="flex justify-end gap-1.5 px-8 py-2.5">
              {trend.examples.map((_, index) => (
                <button
                  key={index}
                  onClick={() => handleDotSelect(index)}
                  className={cn(
                    'h-2 rounded-full transition-all',
                    index === currentImageIndex
                      ? 'w-6 bg-white'
                      : 'w-2 bg-[#4e4e50]',
                  )}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {trend.model && (
        <div className="sticky bottom-9 z-20 px-4 pb-2.5 pt-5">
          <button
            onClick={onRepeat}
            className="flex h-14 w-full items-center justify-center gap-1.5 rounded-[20px]"
            style={{
              background: 'linear-gradient(180deg, #ef6444 0%, #ff7758 100%)',
              border: '1px solid rgba(255,255,255,0.12)',
              boxShadow:
                '-1px 3px 5.25px rgba(0,0,0,0.08), inset 0 1px 0 0 rgba(255,255,255,0.15)',
            }}
          >
            <span className="font-benzin text-base font-semibold text-white">
              ПОВТОРИТЬ
            </span>
          </button>
        </div>
      )}

      <div className="px-4 pt-2.5">
        <div className="space-y-1.5 rounded-xl bg-white/5 p-2.5 px-4">
          <h1 className="font-benzin text-xl font-semibold text-white">
            {trend.name}
          </h1>
          {trend.description && (
            <p className="text-sm leading-[1.57] tracking-tight text-[#e7e8ea]">
              {trend.description}
            </p>
          )}
        </div>
      </div>

      {relatedTrends.length > 0 && (
        <div className="space-y-2 pt-5">
          <div className="px-4">
            <h2 className="font-benzin text-xl font-semibold text-white">
              Похожие
            </h2>
          </div>
          <div className="flex gap-2 overflow-x-auto px-4 scrollbar-hide">
            {relatedTrends.slice(0, 3).map((item) => (
              <RelatedTrendCard key={item.id} trend={item} />
            ))}
          </div>
          {relatedTrends.length > 3 && (
            <div className="flex gap-2 overflow-x-auto px-4 scrollbar-hide">
              {relatedTrends.slice(3, 6).map((item) => (
                <RelatedTrendCard key={item.id} trend={item} />
              ))}
            </div>
          )}
        </div>
      )}

      {footer}
    </div>
  );
}

function RelatedTrendCard({
  trend,
}: {
  trend: TrendDetailScreenRelatedItem;
}) {
  const basePath = useBasePath();
  return (
    <Link
      href={`${basePath}/trends/${trend.id}`}
      className="relative h-60 w-40 flex-shrink-0 overflow-hidden rounded-[20px_4px_20px_4px]"
    >
      {trend.examples[0] &&
        (trend.examples[0].isVideo ? (
          <TrendVideoMedia
            src={trend.examples[0].imageUrl}
            poster={trend.examples[0].thumbnailUrl}
            alt={trend.name}
          />
        ) : (
          <S3Image
            src={trend.examples[0].imageUrl}
            alt={trend.name}
            fill
            className="object-cover"
          />
        ))}
      <div className="absolute bottom-0 left-0 h-[115px] w-full bg-gradient-to-t from-black/[0.87] to-transparent opacity-70" />
      <div className="absolute bottom-2.5 left-2.5 right-2.5 font-benzin text-base font-semibold leading-tight text-white">
        {trend.name}
      </div>
    </Link>
  );
}
